import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import "./css/TopNavbar.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useEnsureValidSession } from "../auth/authorization";

const logoUrl = "https://images.squarespace-cdn.com/content/v1/5cdf852a77196f0001428c44/31c06929-8400-4c8b-9765-dc806758bb58/TOSH_MainLogo_Black_LG_RGB_ForScreens.png?format=500w";

export default function TopNavbar({ }) {
    const { loginWithRedirect, logout, isAuthenticated, isLoading, user, getAccessTokenSilently } = useAuth0();
    const [, setTick] = useState(0);
    const ensureValidSession = useEnsureValidSession();

    // local state to control collapse (replaces Bootstrap JS behavior)
    const [navOpen, setNavOpen] = useState(false);

    useEffect(() => {
        // force a small state update when auth status changes so the navbar UI refreshes
        setTick(t => t + 1);
    }, [isAuthenticated, isLoading, user]);

    useEffect(() => {
        // Debug logging to help diagnose why isAuthenticated is not sticking.
        const clientId = process.env.REACT_APP_AUTH0_CLIENT_ID;
        const cacheKey = `@@auth0spajs@@::${clientId}::default`;
        console.groupCollapsed("Auth0 Debug");
        console.log("isLoading:", isLoading);
        console.log("isAuthenticated:", isAuthenticated);
        console.log("user:", user);
        try {
            const cache = localStorage.getItem(cacheKey);
            console.log("auth0 cache key:", cacheKey);
            console.log("localStorage cache present:", !!cache);
            if (cache) {
                try { console.log("cache:", JSON.parse(cache)); } catch (e) { console.log("cache (raw):", cache); }
            }
        } catch (e) {
            console.log("Error reading localStorage:", e);
        }

        // Only attempt to get an access token silently when the user is authenticated.
        // This avoids "Missing Refresh Token" errors when not authenticated or when no RT was issued.
        (async () => {
            if (!isAuthenticated) {
                console.log("Skipping getAccessTokenSilently: not authenticated");
            } else {
                try {
                    const token = await getAccessTokenSilently();
                    console.log("getAccessTokenSilently OK, token length:", token?.length ?? 0);
                } catch (err) {
                    // log but don't spam the console � this may be expected if the tenant/app isn't configured for refresh tokens
                    console.log("getAccessTokenSilently error:", err && err.message ? err.message : err);
                }
            }
        })();

        if (isAuthenticated) {
            (async () => {
                try {
                    await ensureValidSession();
                } catch (e) {
                    console.error("Session validation failed:", e);
                }
            })();
        }

        console.groupEnd();
    }, [isAuthenticated, isLoading, user, getAccessTokenSilently, ensureValidSession]);

    // small visual indicator for auth state (dev)
    const devBadge = (isLoading ? "Auth: loading" : "");

    // close nav when a link is clicked (mobile friendly)
    const handleNavClick = () => setNavOpen(false);

    return (
        <nav
            className="navbar navbar-expand-md navbar-light bg-light fixed-top shadow-sm"
            // ensure navbar + its dropdown sit above the filter panel
            style={{ zIndex: 1400 }}
        >
            <div className="container-fluid">
                <NavLink className="App-logo navbar-brand d-flex align-items-center" to="/" onClick={handleNavClick}>
                    <img src={logoUrl} className="navbar-logo me-2" alt="The Old School House logo" />
                </NavLink>

                {/* Controlled toggler */}
                <button
                    className="navbar-toggler"
                    type="button"
                    aria-controls="navbarSupportedContent"
                    aria-expanded={navOpen}
                    aria-label="Toggle navigation"
                    onClick={() => setNavOpen(o => !o)}
                >
                    <span className="navbar-toggler-icon" />
                </button>

                {/* Collapse controlled by React state */}
                <div className={`collapse navbar-collapse${navOpen ? " show" : ""}`} id="navbarSupportedContent">
                    <ul className="navbar-nav me-auto mb-2 mb-lg-0" onClick={handleNavClick}>
                        <li className="nav-item">
                            <NavLink to="/map" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Map</NavLink>
                        </li>
                        <li className="nav-item">
                            <NavLink to="/about" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>About</NavLink>
                        </li>

                        {isAuthenticated && (
                            <li className="nav-item">
                                <NavLink to="/manage" className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}>Manage Points</NavLink>
                            </li>
                        )}
                    </ul>

                    <div className="d-flex align-items-center">
                        <div className="me-3 small text-muted d-none d-md-inline">{devBadge}</div>

                        {isLoading ? (
                            <div className="me-2">Loading...</div>
                        ) : (
                            <>
                                {isAuthenticated ? (
                                    <>
                                        <div className="me-2 small text-muted">Hello, {user?.name ?? user?.email}</div>
                                        <button
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => { logout({ returnTo: window.location.origin }); setNavOpen(false); }}
                                        >
                                            Logout
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={() => { loginWithRedirect({ authorizationParams: { prompt: 'login' } }); setNavOpen(false); }}
                                    >
                                        Login
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </nav>
    );
}